import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { IconCheck, IconExternalLink, IconSearch, IconTrash, IconX } from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  Badge,
  BotonExportarExcel,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Group,
  IconAction,
  Input,
  PageHeader,
  PaginadorTabla,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import {
  listarSolicitudesCatalogo,
  type SolicitudDeCatalogo,
} from "@/features/solicitudes-catalogo/services/solicitudes-catalogo.api";
import {
  useResolverSolicitudCatalogo,
  useSolicitudesCatalogoAdmin,
  useSolicitudesCatalogoPendientes,
} from "../hooks/use-solicitudes-catalogo-admin";

const PAGE_SIZE = 15;

const COLOR_ESTADO: Record<string, string> = {
  Pendiente: "ambar",
  Atendida: "exito",
  Rechazada: "peligro",
};

const LABEL_TIPO: Record<string, string> = {
  Proveedor: "Proveedor",
  CuentaContablePorClave: "Cuenta por clave SAT",
};

/** Deep-link al gestor donde el admin da de alta el elemento pedido, por tipo. */
const CATALOGO_HREF: Record<string, string> = {
  Proveedor: "/admin/proveedores",
  CuentaContablePorClave: "/admin/mapeos-clave-cuenta",
};

const FILTROS = ["Pendiente", "Atendida", "Rechazada", "Todas"] as const;

/** Filtro segmentado (pill) · sin átomo `SegmentedControl` compartido (único consumidor en el alcance migrado). */
function FiltroSegmentado({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-input bg-muted/40 p-0.5">
      {FILTROS.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={cn(
            "cursor-pointer rounded-sm px-2.5 py-1 text-xs font-medium transition-colors",
            value === opt ? "bg-card text-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function FilaSolicitud({ s }: { s: SolicitudDeCatalogo }) {
  const navigate = useNavigate();
  const { atender, rechazar, eliminar } = useResolverSolicitudCatalogo();
  const [modal, setModal] = useState<"atender" | "rechazar" | null>(null);
  const [comentario, setComentario] = useState("");

  const cerrar = () => {
    setModal(null);
    setComentario("");
  };

  const confirmar = () => {
    if (modal === "atender")
      atender.mutate(
        { id: s.id, comentario: comentario.trim() || undefined },
        { onSuccess: cerrar }
      );
    else if (modal === "rechazar")
      rechazar.mutate({ id: s.id, comentario: comentario.trim() }, { onSuccess: cerrar });
  };

  const pendiente = s.estado === "Pendiente";

  return (
    <Tr>
      <Td>
        <Badge variant="light" size="sm">
          {LABEL_TIPO[s.tipo] ?? s.tipo}
        </Badge>
      </Td>
      <Td>
        <Text size="sm" fw={600} className="font-mono">
          {s.clave}
        </Text>
        {s.descripcion && (
          <Text size="xs" c="dimmed" className="line-clamp-1" title={s.descripcion}>
            {s.descripcion}
          </Text>
        )}
      </Td>
      <Td>
        <Text size="sm">{s.solicitanteNombre ?? "—"}</Text>
        {s.numeroSolicitudPago && (
          <Text size="xs" c="dimmed">
            Solicitud de pago #{s.numeroSolicitudPago}
          </Text>
        )}
      </Td>
      <Td className="max-w-65">
        <Text size="sm" c={s.comentario ? undefined : "dimmed"} className="line-clamp-2" title={s.comentario ?? undefined}>
          {s.comentario ?? "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{dayjs(s.creadoAt).format("DD/MM/YYYY HH:mm")}</Text>
      </Td>
      <Td>
        <Badge variant="light" color={COLOR_ESTADO[s.estado] ?? "slate"} size="sm">
          {s.estado}
        </Badge>
        {s.respuestaComentario && (
          <Text size="xs" c="dimmed" className="line-clamp-1" title={s.respuestaComentario}>
            {s.respuestaComentario}
          </Text>
        )}
      </Td>
      <Td align="center">
        <Group gap={4} className="flex-nowrap justify-center">
          {CATALOGO_HREF[s.tipo] && pendiente && (
            <IconAction
              label="Ir al catálogo para dar de alta el elemento"
              size="sm"
              onClick={() => void navigate({ to: CATALOGO_HREF[s.tipo] as never })}
            >
              <IconExternalLink size={16} stroke={1.7} />
            </IconAction>
          )}
          {pendiente && (
            <>
              <IconAction label="Marcar atendida" size="sm" iconColor="exito-600" onClick={() => setModal("atender")}>
                <IconCheck size={16} stroke={1.7} />
              </IconAction>
              <IconAction label="Rechazar" size="sm" iconColor="peligro-600" onClick={() => setModal("rechazar")}>
                <IconX size={16} stroke={1.7} />
              </IconAction>
            </>
          )}
          {/* Solo las resueltas se eliminan de la bandeja (soft-hide · el
              solicitante conserva su notificación en la campana). */}
          {!pendiente && (
            <IconAction
              label="Eliminar de mi bandeja"
              size="sm"
              loading={eliminar.isPending && eliminar.variables === s.id}
              onClick={() => eliminar.mutate(s.id)}
            >
              <IconTrash size={16} stroke={1.7} />
            </IconAction>
          )}
        </Group>
      </Td>

      <Dialog open={modal !== null} onOpenChange={(open) => !open && cerrar()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{modal === "atender" ? "Marcar como atendida" : "Rechazar solicitud"}</DialogTitle>
          </DialogHeader>
          <Stack gap="md">
            <Text size="sm">
              {modal === "atender" ? (
                <>
                  Confirma que el elemento <strong>{s.clave}</strong> ya fue dado de alta en el catálogo. El
                  solicitante podrá continuar su solicitud de pago.
                </>
              ) : (
                <>
                  El solicitante verá el motivo del rechazo de <strong>{s.clave}</strong>.
                </>
              )}
            </Text>
            <Textarea
              label={modal === "atender" ? "Comentario (opcional)" : "Motivo del rechazo"}
              placeholder={modal === "atender" ? "ej. Dado de alta como proveedor 10099" : "Obligatorio"}
              value={comentario}
              onChange={(e) => setComentario(e.currentTarget.value)}
              maxLength={500}
              rows={2}
              required={modal === "rechazar"}
            />
          </Stack>
          <DialogFooter>
            <Button variant="default" onClick={cerrar}>
              Cancelar
            </Button>
            <Button
              className={modal === "rechazar" ? "bg-peligro-600 hover:bg-peligro-700" : undefined}
              disabled={modal === "rechazar" && comentario.trim() === ""}
              loading={atender.isPending || rechazar.isPending}
              onClick={confirmar}
            >
              {modal === "atender" ? "Marcar atendida" : "Rechazar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tr>
  );
}

/**
 * Bandeja "Solicitudes de catálogos" (P38) · el administrador recibe aquí las
 * peticiones de los solicitantes para dar de alta elementos que no existen en
 * los catálogos (hoy: proveedores por RFC). Atender = ya dio de alta el
 * elemento (desde su gestor, ej. /admin/proveedores); rechazar exige motivo.
 */
export function AdminSolicitudesCatalogoPage() {
  const [filtro, setFiltro] = useState("Pendiente");
  const [busqueda, setBusqueda] = useState("");
  const [page, setPage] = useState(1);
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  // Estado + búsqueda + paginado SERVER-SIDE (query params con debounce).
  const serverParams = useMemo(
    () => ({
      ...(filtro !== "Todas" ? { estado: filtro } : {}),
      ...(busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [filtro, busquedaDebounced, page],
  );

  // Cambiar los filtros invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [filtro, busquedaDebounced]);

  const { data } = useSolicitudesCatalogoAdmin(serverParams);
  const visibles = data?.items ?? [];
  const total = data?.total ?? 0;

  // El conteo de pendientes viene del endpoint /pendientes/count (mismo que el
  // badge del nav) · el listado ya no trae todos los estados a la vez.
  const pendientes = useSolicitudesCatalogoPendientes(true).data ?? 0;

  // Export a Excel · trae TODAS las páginas con los filtros actuales.
  const obtenerTodas = async (): Promise<SolicitudDeCatalogo[]> => {
    const params = {
      ...(filtro !== "Todas" ? { estado: filtro } : {}),
      ...(busquedaDebounced.trim() ? { busqueda: busquedaDebounced.trim() } : {}),
    };
    const todas: SolicitudDeCatalogo[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarSolicitudesCatalogo({ ...params, page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <PageHeader
        title="Solicitudes de catálogos"
        description="Peticiones de los solicitantes para dar de alta elementos que no existen en los catálogos."
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Administración", href: "/admin" },
          { label: "Solicitudes de catálogos" },
        ]}
      />
      <Stack gap="md">
        <Group justify="space-between" className="flex-wrap">
          <Group gap="sm" className="flex-wrap">
            <FiltroSegmentado value={filtro} onChange={setFiltro} />
            <Input
              placeholder="Buscar por clave, descripción o solicitante…"
              leftSection={<IconSearch size={14} stroke={1.7} />}
              value={busqueda}
              onChange={(e) => setBusqueda(e.currentTarget.value)}
              size="xs"
              wrapperClassName="w-70"
            />
          </Group>
          <Group gap="sm">
            <Text size="sm" c="dimmed">
              {pendientes === 0
                ? "Sin solicitudes pendientes."
                : `${pendientes} solicitud${pendientes === 1 ? "" : "es"} pendiente${pendientes === 1 ? "" : "s"}.`}
            </Text>
            <BotonExportarExcel
              archivo="solicitudes-catalogo"
              hoja="Solicitudes"
              obtenerFilas={obtenerTodas}
              columnas={[
                { header: "Catálogo", value: (s) => s.tipo },
                { header: "Clave", value: (s) => s.clave },
                { header: "Descripción", value: (s) => s.descripcion },
                { header: "Solicitante", value: (s) => s.solicitanteNombre },
                { header: "Solicitud de pago", value: (s) => s.numeroSolicitudPago },
                { header: "Comentario", value: (s) => s.comentario },
                { header: "Fecha", value: (s) => s.creadoAt },
                { header: "Estado", value: (s) => s.estado },
                { header: "Respuesta", value: (s) => s.respuestaComentario },
                { header: "Resuelta el", value: (s) => s.resueltaAt },
              ]}
            />
          </Group>
        </Group>
        <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
        <Card padding="lg">
          <div className="overflow-x-auto">
            <Table verticalSpacing="sm" minWidth={860}>
              <Thead>
                <Tr>
                  <Th>Catálogo</Th>
                  <Th>Elemento solicitado</Th>
                  <Th>Solicitante</Th>
                  <Th>Comentario</Th>
                  <Th>Fecha</Th>
                  <Th>Estado</Th>
                  <Th align="center">Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {visibles.length === 0 ? (
                  <Tr>
                    <Td colSpan={7}>
                      <Text size="sm" c="dimmed" ta="center" className="py-5">
                        No hay solicitudes {filtro === "Todas" ? "" : filtro.toLowerCase() + "s"}.
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  visibles.map((s) => <FilaSolicitud key={s.id} s={s} />)
                )}
              </Tbody>
            </Table>
          </div>
        </Card>

        <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="solicitudes" />
      </Stack>
    </div>
  );
}
